import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Box, Button, FlexV2, Symbol, Text } from '@/primitives'
import Icon from '@/primitives/Icon'
import { setDateFilter } from '@/slices/management/trial'
import Strings from '../../../../Strings'
import { getTrialState } from '@/components/TrialModeView/utils'
import DateSyncButton from './DateSyncButton'
import {
  getAvailableDateRanges,
  getDateRangeOptions,
  getDefaultDateFilters,
  getIntervalOptions,
  getRecommendedTimeInterval
} from '../utils/dateFilterUtils'
import './DateFilterDialog.scss'
import { DatePicker } from './DatePicker'
import { FilterSelect } from './FilterSelect'

const DateFilterDialog = ({ open, onOpenChange, dialogMode }) => {
  const strings = Strings()
  const dispatch = useDispatch()
  const { dateFilterEnv, dateFilterSoil, dateFiltersSynced, currentTrial } =
    useSelector(getTrialState)

  const isEnvironmental = dialogMode === 'envirosense'
  const currentFilter = isEnvironmental ? dateFilterEnv : dateFilterSoil
  const timeZone = currentTrial.zone.timeZone
  const [localFilter, setLocalFilter] = useState(
    currentFilter || getDefaultDateFilters()
  )

  useEffect(() => {
    setLocalFilter(currentFilter || getDefaultDateFilters())
  }, [currentFilter, dialogMode])

  const updateFilter = updates => {
    setLocalFilter(prev => ({
      ...prev,
      ...updates
    }))
  }

  const setDate = (date, name) => {
    const adjustedTime = DateTime.fromJSDate(date).startOf('minute').toMillis()
    const fromDate = name === 'fromDate' ? adjustedTime : localFilter.fromDate
    const toDate = name === 'toDate' ? adjustedTime : localFilter.toDate
    updateFilter({
      [name]: adjustedTime,
      dateRange: 'customRange',
      timeInterval: getRecommendedTimeInterval(fromDate, toDate)
    })
  }

  const onChangeDateRange = selectedItem => {
    const range = selectedItem?.value
    if (range !== 'customRange') {
      const { fromDate, toDate } = getDateRangeOptions(range)
      updateFilter({
        dateRange: range,
        fromDate,
        toDate,
        timeInterval: getRecommendedTimeInterval(fromDate, toDate)
      })
    } else {
      updateFilter({ dateRange: range })
    }
  }

  const onChangeFromDate = date => {
    setDate(date, 'fromDate')
    const diff = DateTime.fromJSDate(date).diff(
      DateTime.fromMillis(localFilter.toDate),
      'minutes'
    )
    if (diff.values.minutes >= 0) {
      const plus = diff.toObject().milliseconds
        ? diff.toObject()
        : { minutes: 15 }
      const maxDate = DateTime.now().setZone(timeZone)
      let newToDate = DateTime.fromJSDate(date).plus(plus).toJSDate()
      if (
        maxDate.diff(DateTime.fromJSDate(date), 'minutes').values.minutes <= 30
      ) {
        const remainder = maxDate.minute % 15
        newToDate = maxDate.minus({ minutes: remainder }).toJSDate()
      }
      setDate(newToDate, 'toDate')
    }
  }

  if (!open) return null

  const dateRangeOptions = getAvailableDateRanges().map(range => ({
    value: range,
    label: strings[range] || range
  }))

  const timeIntervalOptions = getIntervalOptions(
    getRecommendedTimeInterval(localFilter.fromDate, localFilter.toDate)
  )

  const maxDate = DateTime.now().setZone(timeZone)

  return (
    <Box className='DateFilterDialog__Overlay'>
      <Box className='DateFilterDialog__Content'>
        <Box className='DateFilterDialog__Header'>
          <FlexV2 alignMainAxis='space-between' axisGap={200}>
            <FlexV2 alignCrossAxis='center'>
              <Icon
                name={isEnvironmental ? 'thermostat' : 'water_drop'}
                size={400}
              />
              <Text size={400} fontWeight={600}>
                {strings[`${dialogMode}DateFilterTitle`]}
              </Text>
            </FlexV2>
            <Button variant='text' onClick={() => onOpenChange(false)}>
              <Icon name='close' size={300} />
            </Button>
          </FlexV2>
        </Box>

        <Box className='DateFilterDialog__ComparisonRow__Content'>
          <FlexV2 direction='column' axisGap={300}>
            <FilterSelect
              value={localFilter.dateRange}
              onChange={onChangeDateRange}
              options={dateRangeOptions}
              label={strings.dateRangeLabel}
              isDisabled={dateFiltersSynced && !isEnvironmental}
            />
            <DatePicker
              type='from'
              localFilter={localFilter}
              onChangeFromDate={onChangeFromDate}
              onChangeToDate={date => setDate(date, 'toDate')}
              maxDate={maxDate}
              strings={strings}
              timeZone={timeZone}
              dateFiltersSynced={dateFiltersSynced}
              isEnvironmental={isEnvironmental}
            />
            <DatePicker
              type='to'
              localFilter={localFilter}
              onChangeFromDate={onChangeFromDate}
              onChangeToDate={date => setDate(date, 'toDate')}
              maxDate={maxDate}
              strings={strings}
              timeZone={timeZone}
              dateFiltersSynced={dateFiltersSynced}
              isEnvironmental={isEnvironmental}
            />
            <FilterSelect
              value={localFilter.timeInterval}
              onChange={item => updateFilter({ timeInterval: item?.value })}
              label={strings.graphIntervalLabel}
              options={timeIntervalOptions}
              isDisabled={dateFiltersSynced && !isEnvironmental}
            />
            <FlexV2 axisGap={200} alignCrossAxis='center'>
              <Box>
                <Symbol name='info' variant='page' tone={900} />
              </Box>
              <Text variant='page' tone={900}>
                {strings.timeZoneFetchInfo}:{' '}
                <Text as='span' fontWeight={600}>
                  {timeZone}
                </Text>
              </Text>
            </FlexV2>
          </FlexV2>
        </Box>

        <Box className='DateFilterDialog__Footer'>
          {dateFiltersSynced && !isEnvironmental && (
            <FlexV2
              alignCrossAxis='center'
              className='DateFilterDialog__SyncedMessage'
              axisGap={200}
            >
              <Icon name='warning' size={200} tone={600} variant='info' />
              <Text size={200} tone={600} variant='info'>
                {strings.syncedFilterInfo}
              </Text>
            </FlexV2>
          )}
          <FlexV2 alignMainAxis='space-between' axisGap={200}>
            <DateSyncButton onSync={() => onOpenChange(false)} />
            <FlexV2 axisGap={200}>
              <Button variant='outline' onClick={() => onOpenChange(false)}>
                {strings.cancel}
              </Button>
              <Button
                variant='primary'
                onClick={() => {
                  dispatch(
                    setDateFilter({
                      type: isEnvironmental ? 'envirosense' : 'soil',
                      filter: localFilter
                    })
                  )
                  onOpenChange(false)
                }}
                disabled={dateFiltersSynced && !isEnvironmental}
              >
                {strings.apply}
              </Button>
            </FlexV2>
          </FlexV2>
        </Box>
      </Box>
    </Box>
  )
}

export default DateFilterDialog
